import PropTypes from 'prop-types'
import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import BookList from './BookList'
import { useBookSeat } from '../../common/hooks'
import { getCurrentUserId } from '../../api/auth-service'
import BookingConfirmation from './BookingConfirmation'
import './index.css'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '@/common/components/ui/card';

const VISIBLE_COUNT = 2

function BookListStatus({ data, onRefresh }) {
  const { bookings, status, error } = data
  const userId = getCurrentUserId()
  const [, , removeSeatBook] = useBookSeat()
  const [showAll, setShowAll] = useState(false)
  const [confirmationModal, setConfirmationModal] = useState({
    show: false,
    seatInfo: null,
    isLoading: false
  })

  const showCancelConfirmation = (bookingInfo) => {
    setConfirmationModal({
      show: true,
      seatInfo: {
        seatId: bookingInfo.seatId,
        rowId: 'N/A',
        date: bookingInfo.date,
        officeName: bookingInfo.officeName
      },
      isLoading: false
    })
  }

  const hideConfirmation = () => {
    setConfirmationModal({ show: false, seatInfo: null, isLoading: false })
  }

  const handleConfirmCancellation = () => {
    const { seatId, date } = confirmationModal.seatInfo
    setConfirmationModal(prev => ({ ...prev, isLoading: true }))

    removeSeatBook(date, seatId, userId)
      .then(
        () => {
          hideConfirmation()
          if (onRefresh) onRefresh()
        },
        error => {
          console.error('handleConfirmCancellation', error)
          hideConfirmation()
        }
      )
  }

  if (status.isLoading)
    return (
      <Card className="w-full rounded-2xl border border-border/60 py-2 shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl">My Bookings</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )

  if (status.isRejected)
    return (
      <Card className="w-full rounded-2xl border border-border/60 py-2 shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl">My Bookings</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 pb-5">
          <div className="mt-[50px] ml-[40px]">
            <div className="text-red-600">
              User booking list request failed! {error.response?.data ?? error.message}
            </div>
          </div>
        </CardContent>
      </Card>
    )

  if (status.isResolved) {
    const hasMore = bookings?.length > VISIBLE_COUNT
    const visibleBookings = showAll ? bookings : bookings?.slice(0, VISIBLE_COUNT)
    
    return (
      <>
        <Card className="w-full rounded-2xl border border-border/60 py-2 shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl">My Bookings</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 pb-5">
            <div className="max-h-[70vh] overflow-y-auto p-1">
              <BookList bookings={visibleBookings} onCancelBooking={showCancelConfirmation} />
            </div>
            {hasMore && (
              <button
                className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors self-start"
                onClick={() => setShowAll(prev => !prev)}
              >
                {showAll ? 'Show less' : `Show all (${bookings.length})`}
              </button>
            )}
          </CardContent>
        </Card>

        <BookingConfirmation
          show={confirmationModal.show}
          onCancel={hideConfirmation}
          onConfirm={handleConfirmCancellation}
          seatInfo={confirmationModal.seatInfo}
          isLoading={confirmationModal.isLoading}
          actionType="cancel"
        />
      </>
    )
  }

  return null
}

BookListStatus.propTypes = {
  data: PropTypes.shape({
    bookings: PropTypes.array,
    status: PropTypes.shape({
      isIdle: PropTypes.bool,
      isLoading: PropTypes.bool,
      isResolved: PropTypes.bool,
      isRejected: PropTypes.bool,
    }).isRequired,
    error: PropTypes.shape({
      message: PropTypes.string,
      response: PropTypes.object,
    }),
  }).isRequired,
  onRefresh: PropTypes.func,
}

export default BookListStatus
